import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { createTransport } from '@sentry/core';
import store from './store/store';
import { ThemeProvider } from 'theme-ui';
import { theme } from 'theme/theme';
import { Router } from 'routing/Router';
import { Flex } from 'rebass';
import { Toaster } from 'react-hot-toast';
import { PwaUpdatePrompt } from 'components/PwaUpdatePrompt/PwaUpdatePrompt';
import { AppErrorFallback } from 'components/AppErrorFallback/AppErrorFallback';
import { SkipLink } from 'components/SkipLink/SkipLink';
import { FeedbackButton } from 'components/FeedbackButton/FeedbackButton';
import { isDevelopmentMode } from 'utils/isDevelopmentMode';
import { baseURL } from 'api/api';
import { scrubBreadcrumb } from 'utils/logging/scrubBreadcrumb';
import { getSentryEnvironment } from 'utils/logging/getSentryEnvironment';

function makeFetchTransport(options: any) {
  function makeRequest(request: any) {
    const requestOptions: RequestInit = {
      body: request.body,
      method: 'POST',
      referrerPolicy: 'origin',
      headers: options.headers || {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      ...options.fetchOptions,
    };

    return fetch(options.url, requestOptions).then(response => {
      return {
        statusCode: response.status,
        headers: {
          'x-sentry-rate-limits': response.headers.get('X-Sentry-Rate-Limits'),
          'retry-after': response.headers.get('Retry-After'),
        },
      };
    });
  }

  return createTransport(options, makeRequest);
}

if (!isDevelopmentMode) {
  Sentry.init({
    dsn: 'https://84f838f5f9e1148dafb66fc0b0d3517b@o4505450104094720.ingest.us.sentry.io/4505987418226688',
    // Until now every event from both deployments landed in one undifferentiated
    //   stream, so a staging experiment and a production incident looked alike.
    //   That cost real time last week.
    environment: getSentryEnvironment(baseURL),
    // Safe alongside the sourcemap upload: `sentry-cli sourcemaps inject` writes
    //   Debug IDs into the bundles — confirmed present in the live production
    //   bundle — and symbolication resolves through those, not through the
    //   release name. So this adds a filter and regression tracking without
    //   touching how stack traces are resolved.
    release: __APP_VERSION__,
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      // eslint-disable-next-line
      Sentry.reactRouterV5BrowserTracingIntegration({ history }),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        // Disable the default floating actor button — we render our own
        // (FeedbackButton) so we can place it above the tab bar in the
        // bottom-left and keep it from overlapping navigation.
        autoInject: false,
        colorScheme: 'system',
        useSentryUser: {
          name: 'name',
          email: 'email',
        },
        onFormOpen: () => {
          // Hide the name and email fields
          const shadowRoot =
            document.querySelector('#sentry-feedback')?.shadowRoot;
          if (shadowRoot) {
            shadowRoot
              .querySelector('label[htmlFor="name"]')
              ?.setAttribute('style', 'display: none');
            shadowRoot
              .querySelector('label[htmlFor="email"]')
              ?.setAttribute('style', 'display: none');
          }
        },
      }),
    ],
    debug: isDevelopmentMode,
    tunnel: `${baseURL}/sentry`,
    transport: makeFetchTransport,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.petehealth\.com\/api/,
    ],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Console breadcrumbs ship `data.arguments` — the raw argument list — and
    //   Session Replay records them too, so anything passed to `console.error`
    //   leaves the device. Call sites log summaries (`summariseErrorBody`);
    //   this is the backstop for the ones that do not.
    beforeBreadcrumb: scrubBreadcrumb,
  });
}
// To get the correct height on mobile devices
// Hacky but functional for now
const setVerticalHeight = () => {
  // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
  const vh = window.innerHeight * 0.01;
  // Then we set the value in the --vh custom property to the root of the document
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};
setVerticalHeight();

export const App: React.FC = () => {
  useEffect(() => {
    window.addEventListener('resize', setVerticalHeight);
    window.addEventListener('orientationchange', setVerticalHeight);
    return () => {
      window.removeEventListener('resize', setVerticalHeight);
      window.removeEventListener('orientationchange', setVerticalHeight);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary
      showDialog={false}
      fallback={({ error, eventId, resetError }) => (
        <AppErrorFallback
          error={error}
          eventId={eventId}
          resetError={resetError}
        />
      )}>
      <SkipLink targetId="main-content" />
      <Flex as="main" id="main-content" height="100%">
        <ThemeProvider theme={theme}>
          <Provider store={store}>
            <Router />
          </Provider>
        </ThemeProvider>
      </Flex>
      <FeedbackButton />
      <PwaUpdatePrompt />
      <Toaster
        toastOptions={{
          loading: {
            style: {
              background: '#348ce9',
              color: 'white',
            },
          },
          error: {
            style: {
              background: '#e24747',
              color: 'white',
            },
          },
        }}
      />
    </Sentry.ErrorBoundary>
  );
};
